/**
 * ReviewShell — gate detail / decision page (P9c).
 *
 * Routed at /reviews/:gateId. Loads the gate via apiv1.hitl.getGate(),
 * renders metadata + payload + Approve / Reject / Request Changes
 * controls. Uses gate_ui_hints (when present on the gate's
 * reasoning_chain) to highlight specific payload fields and override
 * the primary action label.
 *
 * P9c keeps this page self-contained rather than wrapping the existing
 * `ReviewShell` shared component (which assumes Story 4.7's plugin
 * registry). A follow-on can swap in the rich 3-panel ReviewShell once
 * the plugin registry is wired to v2 skills.
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { hitl, type GateResponse, type GateUiHints } from "../../../apiv1";

interface Props {
  onBack?: () => void;
}

type DecisionState = "idle" | "approving" | "rejecting" | "submitting";

export function GateDetail({ onBack }: Props) {
  const navigate = useNavigate();
  const params = useParams<{ gateId: string }>();
  const gateId = params.gateId;

  const [gate, setGate] = useState<GateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // Fatal load error — the gate fetch failed, so there's no form to show.
  // Renders the full-screen error view with "Back to reviews".
  const [loadError, setLoadError] = useState<string | null>(null);
  // Inline decision error — client-side validation (notes-required) or a
  // failed decision submit. Renders as a banner above the still-mounted
  // decision controls so the reviewer can correct and retry in place.
  const [decisionError, setDecisionError] = useState<string | null>(null);

  const [decisionState, setDecisionState] = useState<DecisionState>("idle");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!gateId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const fetched = await hitl.getGate(gateId);
        if (!cancelled) setGate(fetched);
      } catch (e) {
        if (!cancelled)
          setLoadError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gateId]);

  if (!gateId) {
    return <div className="p-6 text-white/40 text-sm">No gate id in URL.</div>;
  }
  if (loading) {
    return <div className="p-6 text-white/40 text-sm">Loading gate…</div>;
  }
  if (loadError) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded">
          {loadError}
        </div>
        <button
          onClick={() => (onBack ? onBack() : navigate("/reviews"))}
          className="px-3 py-1.5 bg-white/10 hover:bg-white/15 text-sm rounded"
        >
          Back to reviews
        </button>
      </div>
    );
  }
  if (!gate) {
    return <div className="p-6 text-white/40 text-sm">Gate not found.</div>;
  }

  const hints: GateUiHints =
    (gate.reasoning_chain?.gate_ui_hints as GateUiHints | undefined) ?? {};
  const highlightFields = hints.highlight_fields ?? [];
  const primaryActionLabel = hints.primary_action_label ?? "Approve";
  const stageName =
    (gate.reasoning_chain?.stage_name as string | undefined) ?? "—";
  const stageDescription =
    (gate.reasoning_chain?.stage_description as string | undefined) ?? "";

  const isTerminal = gate.status !== "pending";

  async function submit(decision: "approved" | "rejected") {
    if (!gateId) return;
    if (decision === "rejected" && !notes.trim()) {
      setDecisionError("Notes are required when rejecting a gate.");
      return;
    }
    setDecisionError(null);
    setDecisionState("submitting");
    try {
      const updated = await hitl.decideGate(gateId, {
        decision,
        notes: notes.trim() || null,
      });
      setGate(updated);
      setDecisionState("idle");
    } catch (e) {
      setDecisionError(e instanceof Error ? e.message : String(e));
      setDecisionState("idle");
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => (onBack ? onBack() : navigate("/reviews"))}
          className="text-xs text-white/40 hover:text-white/70"
        >
          ← Back to reviews
        </button>
        <span className="text-xs font-mono text-white/30">{gate.id}</span>
      </div>

      <h1 className="text-2xl font-semibold mb-1">{stageName}</h1>
      {stageDescription && (
        <p className="text-sm text-white/60 mb-6">{stageDescription}</p>
      )}

      {/* Gate metadata */}
      <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-white/5 border border-white/10 rounded">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/40">
            Agent
          </div>
          <div className="text-sm font-mono">{gate.agent_id}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/40">
            Confidence
          </div>
          <div className="text-sm font-mono">
            {gate.confidence_score.toFixed(3)}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/40">
            Affected rows
          </div>
          <div className="text-sm font-mono">
            {gate.affected_row_count.toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/40">
            Status
          </div>
          <div className="text-sm capitalize">
            {gate.status}
            {gate.escalation_level > 0 && (
              <span className="ml-2 text-xs text-orange-400">
                escalated L{gate.escalation_level}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Highlighted fields per gate_ui_hints */}
      {highlightFields.length > 0 && gate.output_data_sample && (
        <div className="mb-6 p-4 bg-amber-500/5 border border-amber-500/20 rounded">
          <div className="text-[11px] uppercase tracking-wider text-amber-300 mb-2">
            Key fields
          </div>
          <div className="grid grid-cols-2 gap-3">
            {highlightFields.map((field) => {
              const value = (
                gate.output_data_sample as Record<string, unknown>
              )?.[field];
              return (
                <div key={field}>
                  <div className="text-[11px] text-white/40">{field}</div>
                  <div className="text-sm font-mono break-all">
                    {value === undefined
                      ? "—"
                      : typeof value === "object"
                        ? JSON.stringify(value)
                        : String(value)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Output payload (full JSON viewer) */}
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2">
          Proposed output
        </div>
        <pre className="p-3 bg-black/40 border border-white/10 rounded text-xs font-mono overflow-x-auto max-h-96">
          {JSON.stringify(gate.output_data_sample ?? {}, null, 2)}
        </pre>
      </div>

      {/* Decision row */}
      {!isTerminal ? (
        <div className="p-4 bg-white/5 border border-white/10 rounded">
          {decisionError && (
            <div
              className="mb-3 p-3 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded"
              role="alert"
              data-testid="decision-error"
            >
              {decisionError}
            </div>
          )}
          <label className="block text-[11px] uppercase tracking-wider text-white/40 mb-2">
            Notes (required on reject)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Capture your reasoning so the audit trail is complete."
            className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded text-sm font-mono"
          />
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => submit("rejected")}
              disabled={decisionState === "submitting"}
              className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-200 text-sm rounded disabled:opacity-50"
              data-testid="reject-button"
            >
              Reject
            </button>
            <button
              onClick={() => submit("approved")}
              disabled={decisionState === "submitting"}
              className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-sm rounded disabled:opacity-50"
              data-testid="approve-button"
            >
              {primaryActionLabel}
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-white/5 border border-white/10 rounded text-sm">
          <div className="text-white/60">
            Decided{" "}
            {gate.decision_at
              ? new Date(gate.decision_at).toLocaleString()
              : ""}{" "}
            by{" "}
            <span className="font-mono text-xs">
              {gate.reviewer_id ?? "system"}
            </span>
          </div>
          {gate.notes && (
            <div className="mt-2 text-xs text-white/50">{gate.notes}</div>
          )}
        </div>
      )}
    </div>
  );
}
